<!--
  International Auctions — lot profile (BXS-3627 · F1.4).

  `@if` / `@for` throughout, and NOT one `<ng-container>` or `<ng-template>`:
  `npm run lint:templates` (the BXS-3584 guard) rejects host-element bindings on
  container nodes, and the built-in blocks make the whole class of mistake
  impossible rather than merely caught.

  Loading is checked FIRST, so the not-found state can never flash while the
  request is still in flight.
-->
<section class="ia-lot">
  @if (isLoading) {
    <!-- Shape-of-the-page skeleton: the mock service answers on a real ~250 ms
         delay, so this is a state a visitor actually sees. -->
    <div class="ia-lot__skeleton" aria-live="polite">
      <span class="ia-lot__srOnly">{{ 'common.loading' | translate }}</span>
      <span class="ia-lot__skelBand"></span>
      <div class="ia-lot__skelHero">
        <span class="ia-lot__skelStage"></span>
        <div class="ia-lot__skelSide">
          <span class="ia-lot__skelLine is-wide"></span>
          <span class="ia-lot__skelLine"></span>
          <span class="ia-lot__skelBlock"></span>
          <span class="ia-lot__skelLine"></span>
          <span class="ia-lot__skelLine"></span>
          <span class="ia-lot__skelLine"></span>
        </div>
      </div>
    </div>
  } @else if (lot) {
    <!-- ===== Co-branded house band =====================================
         The house's colour FILLS this band — the same band the catalogue and
         the house page open with, in the compact form the horse-profile mockup
         draws. All four colours arrive as custom properties from
         `resolveHouseBrand()`; not one partner hex appears in the stylesheet. -->
    <div
      class="ia-lot__band"
      [style.--ia-brand]="brand.color"
      [style.--ia-ink]="brand.ink"
      [style.--ia-accent]="brand.accent"
      [style.--ia-accent-ink]="brand.accentInk"
    >
      <div class="ia-lot__bandId">
        <span class="ia-lot__plaque" [class.is-bare]="brand.logoOnBrand">
          @if (logoUrl) {
            <img
              class="ia-lot__logo"
              [src]="logoUrl"
              [alt]="houseName"
              loading="lazy"
              decoding="async"
            />
          } @else {
            <span class="ia-lot__wordmark">{{ houseName }}</span>
          }
        </span>
        <span class="ia-lot__bandText">
          <span class="ia-lot__bandEyebrow">{{ lotPill }}</span>
          @if (saleName) {
            <span class="ia-lot__bandSale">{{ saleName }}</span>
          }
          @if (saleMeta) {
            <span class="ia-lot__bandMeta">{{ saleMeta }}</span>
          }
        </span>
      </div>

      <!-- The single most commercially important click in this module. It goes
           through the server-side redirect that logs it and appends UTM, never
           straight to the house — and as a plain href, because a routerLink
           would have the client router swallow a URL that is not ours.

           Absent, not disabled, when the lot has no page at the house: the
           redirect 404s a row it holds no destination for, and a button that
           opens an error is worse than no button. -->
      <div class="ia-lot__bandActions">
        <!-- "Put it in my list" (M19). Beside the outbound CTA rather than up
             with the title, because the two are the only two things a visitor
             can DO from this band and they belong together. Shown to guests as
             well, where it leads to the login page — hiding it would
             reproduce the very "ما في" Saeed complained about for anyone not
             signed in yet. -->
        @if (lotId) {
          <app-international-favourite-button
            [lotId]="lotId"
            variant="bar"
            [showLabel]="true"
          ></app-international-favourite-button>
        }

        @if (goHref) {
          <a
            class="ia-lot__bandCta"
            [href]="goHref"
            target="_blank"
            rel="noopener"
          >
            <span>{{
              'international_auctions.lot.bid_on' | translate: bidOnParams
            }}</span>
            <span class="ia-lot__arrow" aria-hidden="true">&#8599;</span>
          </a>
        }
      </div>
    </div>

    <!-- ===== Preview-only disclaimer =================================== -->
    <div class="ia-lot__notice">
      <span class="ia-lot__noticeChip">{{
        'international_auctions.preview_only' | translate
      }}</span>
      <span class="ia-lot__noticeText">{{
        'international_auctions.disclaimer' | translate
      }}</span>
    </div>

    <!-- ===== Breadcrumb + prev/next ==================================== -->
    <div class="ia-lot__crumbrow">
      <nav class="ia-lot__crumb">
        <a [routerLink]="indexLink | localeLink">{{
          'international_auctions.title' | translate
        }}</a>
        <span class="ia-lot__crumbSep" aria-hidden="true"></span>
        <a [routerLink]="houseLink | localeLink">{{ houseName }}</a>
        @if (saleName) {
          <span class="ia-lot__crumbSep" aria-hidden="true"></span>
          <a [routerLink]="saleLink | localeLink">{{ saleName }}</a>
        }
        <span class="ia-lot__crumbSep" aria-hidden="true"></span>
        <span class="ia-lot__crumbHere">{{ lotPill }}</span>
      </nav>

      <!-- Prev/Next STAYS — it is still the right control for browsing. The
           jump box sits beside it for the other gesture: 173 lots at
           Tattersalls, ~4,500 at Keeneland, and a visitor who was told to look
           at 147 (CEO review, 18 Aug 2026). -->
      <div class="ia-lot__pager">
        <app-lot-jump
          variant="ia"
          [lotLabel]="lotJumpLabel || ('lot_jump.lot' | translate)"
          [entries]="lotJumpEntries"
          [loading]="lotJumpLoading"
          (load)="onLotJumpLoad()"
          (jump)="onLotJump($event)"
        />

        <!-- Either control is ABSENT at the end of the catalogue. A dead
             button is worse than no button. -->
        @if (previousLink) {
          <a class="ia-lot__pagerLink" [routerLink]="previousLink | localeLink">
            <span class="ia-lot__pagerArrow" aria-hidden="true">&#8592;</span>
            <span>{{
              'international_auctions.lot.previous_lot' | translate
            }}</span>
          </a>
        }
        @if (nextLink) {
          <a class="ia-lot__pagerLink" [routerLink]="nextLink | localeLink">
            <span>{{ 'international_auctions.lot.next_lot' | translate }}</span>
            <span class="ia-lot__pagerArrow" aria-hidden="true">&#8594;</span>
          </a>
        }
      </div>
    </div>

    <!-- ===== Hero: media stage + identity ============================== -->
    <div class="ia-lot__hero">
      <!-- --- media column --- -->
      <div class="ia-lot__mediaCol">
        <!-- The stage is the shape of the PHOTOGRAPH, not a fixed landscape
             box: 155 of this sale's 377 photographs are portrait, and a
             centred crop of a portrait horse keeps the barrel and loses the
             head and the legs (18 Aug review). `stageRatio` clamps the
             extremes and falls back to 3:2. -->
        <div class="ia-lot__stage" [style.aspect-ratio]="stageRatio">
          @if (noMedia) {
            <!-- THE NORMAL CASE. Neither surveyed Tattersalls sale published a
                 single image, so this is a finished state: house monogram on a
                 quiet hatch, and a sentence explaining why. No broken-image
                 glyph, no empty grey box, no "0 photos". -->
            <div class="ia-lot__noMedia" [style.--ia-brand]="brandColor">
              <span class="ia-lot__monogram" aria-hidden="true">{{
                monogram
              }}</span>
              <span class="ia-lot__noMediaTitle">{{
                'international_auctions.lot.no_media' | translate
              }}</span>
              <span class="ia-lot__noMediaHint">{{
                'international_auctions.lot.no_media_hint' | translate
              }}</span>
            </div>
          } @else if (activeMedia) {
            @if (activeMedia; as item) {
              @if (item.kind === 'photo') {
              <button
                type="button"
                class="ia-lot__stageBtn"
                (click)="openPhoto(item)"
              >
                <!-- Only ever visible where the clamp stopped the box from
                     following the image — a very tall or very wide shot. A
                     blurred copy of the photograph itself reads as depth of
                     field; flat grey reads as a mistake. `aria-hidden` and no
                     alt: it is the same picture, said twice. -->
                <img
                  class="ia-lot__stageWash"
                  [src]="item.url"
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                />
                <img
                  class="ia-lot__stageImg"
                  [src]="item.url"
                  [alt]="item.caption || title"
                  decoding="async"
                />
                <span class="ia-lot__stageZoom" aria-hidden="true">&#10530;</span>
              </button>
            } @else if (item.video) {
              @if (isPlaying(item.video)) {
                @if (item.video.kind === 'external') {
                  <iframe
                    class="ia-lot__embed"
                    [src]="item.video.url | safeUrl"
                    [title]="item.video.title || title"
                    allow="accelerometer; encrypted-media; picture-in-picture"
                    allowfullscreen
                  ></iframe>
                } @else {
                  <video
                    class="ia-lot__embed"
                    [src]="item.video.url"
                    [attr.poster]="item.video.poster_url"
                    controls
                  ></video>
                }
              } @else {
                <!-- Click-to-load: nothing is fetched from a third party, and
                     no poster request is made, until the visitor asks. -->
                <button
                  type="button"
                  class="ia-lot__play"
                  [style.--ia-brand]="brandColor"
                  (click)="playVideo(item.video)"
                >
                  @if (item.video.poster_url) {
                    <img
                      class="ia-lot__stageImg"
                      [src]="item.video.poster_url"
                      [alt]="item.video.title || title"
                      decoding="async"
                    />
                  }
                  <span class="ia-lot__playGlyph" aria-hidden="true">&#9654;</span>
                  <span class="ia-lot__playLabel">{{
                    item.video.title ||
                      ('international_auctions.lot.tab_video' | translate)
                  }}</span>
                </button>
              }
            }
          }
          }

          <span class="ia-lot__stagePill">{{ lotPill }}</span>
        </div>

        @if (activeMedia?.caption) {
          <p class="ia-lot__caption">{{ activeMedia?.caption }}</p>
        }

        <!-- One image is not a gallery, so the strip only exists above one. -->
        @if (media.length > 1) {
          <div class="ia-lot__thumbs">
            @for (item of media; track item.id; let i = $index) {
              <button
                type="button"
                class="ia-lot__thumb"
                [class.is-active]="i === activeMediaIndex"
                [attr.aria-current]="i === activeMediaIndex ? 'true' : null"
                (click)="selectMedia(i)"
              >
                @if (item.thumbUrl) {
                  <img
                    class="ia-lot__thumbImg"
                    [src]="item.thumbUrl"
                    [alt]="item.caption || title"
                    loading="lazy"
                    decoding="async"
                  />
                } @else {
                  <span class="ia-lot__thumbGlyph" aria-hidden="true"
                    >&#9654;</span
                  >
                }
                @if (item.caption) {
                  <span class="ia-lot__thumbCaption">{{ item.caption }}</span>
                }
              </button>
            }
          </div>
        }
      </div>

      <!-- --- identity column --- -->
      <div class="ia-lot__infoCol">
        <!-- Heading and lot label share ONE line, the label at the trailing
             edge. The `h1` comes first in the DOM so the heading is still the
             first thing a screen reader meets. -->
        <div class="ia-lot__titleRow">
          <h1 class="ia-lot__title">{{ title }}</h1>
          <span class="ia-lot__eyebrow">{{ lotPill }}</span>
        </div>

        @if (sireName || damName) {
          <p class="ia-lot__breeding">
            @if (sireName) {
              <span class="ia-lot__sire">{{ sireName }}</span>
            }
            @if (sireName && damName) {
              <span class="ia-lot__cross" aria-hidden="true">&#10005;</span>
            }
            @if (damName) {
              <span class="ia-lot__dam">{{ damName }}</span>
            }
            @if (sireOfDamName) {
              <span class="ia-lot__damSire">({{ sireOfDamName }})</span>
            }
          </p>
        }

        <!-- Timeform's rating gets an element of its own, not a table row: on a
             timed sale it is a column, a filter and a sort key on the house's
             own page, and it is what a buyer scans the catalogue for. -->
        @if (timeformRating) {
          <p class="ia-lot__timeform">
            <span class="ia-lot__timeformLabel">{{
              'international_auctions.lot.timeform' | translate
            }}</span>
            <span class="ia-lot__timeformValue">{{ timeformRating }}</span>
          </p>
        }

        <!-- A withdrawn lot has no outcome and no price. Saying so plainly is
             the whole content of the page. -->
        @if (isWithdrawn) {
          <p class="ia-lot__withdrawn">
            <span class="ia-lot__withdrawnMark" aria-hidden="true">&#9888;</span>
            <span>{{
              'international_auctions.lot.withdrawn_notice' | translate
            }}</span>
          </p>
        } @else {
          <!-- The outcome is stated FIRST, label and chip on ONE line. The
               price below is a published figure, not a claim that the lot
               sold. -->
          <div class="ia-lot__price" [class]="statusModifier">
            <!-- On a timed sale this is the fact the whole page turns on: lots
                 close minutes apart, so the deadline is THIS lot's, not the
                 sale's. It leads the block, above the outcome, and it carries
                 its zone because "11:00" alone is not actionable abroad. -->
            @if (biddingEndsLabel) {
              <div
                class="ia-lot__closing"
                [class.is-closed]="biddingHasEnded"
              >
                <span class="ia-lot__closingLabel">{{
                  biddingEndsKey | translate
                }}</span>
                <span class="ia-lot__closingValue">{{ biddingEndsLabel }}</span>
              </div>
            }

            <div class="ia-lot__outcomeRow">
              <span class="ia-lot__priceEyebrow">{{
                'international_auctions.lot.outcome' | translate
              }}</span>
              <span class="ia-lot__status">
                <span class="ia-lot__statusDot" aria-hidden="true"></span>
                <span>{{ statusKey | translate }}</span>
              </span>
            </div>

            <dl class="ia-lot__priceRows">
              <dt>{{ 'international_auctions.lot.price' | translate }}</dt>
              <dd class="ia-lot__priceValue">
                @if (priceLabel) {
                  {{ priceLabel }}
                } @else {
                  <span class="ia-lot__priceNone">{{
                    'international_auctions.lot.no_price' | translate
                  }}</span>
                }
              </dd>

              @if (purchaserName) {
                <dt>{{ 'international_auctions.sale.purchaser' | translate }}</dt>
                <dd>{{ purchaserName }}</dd>
              }
            </dl>

            <!-- The house crams status, buyer and the private-sale marker into
                 one cell. We keep the original rather than lose what parsing
                 drops. -->
            @if (purchaserRaw) {
              <div class="ia-lot__raw">
                <span class="ia-lot__rawLabel">{{
                  'international_auctions.lot.as_published' | translate
                }}</span>
                <span class="ia-lot__rawValue">{{ purchaserRaw }}</span>
              </div>
            }
          </div>
        }

        @if (consignorName || contactName || contactPhone || contactEmail) {
          <div class="ia-lot__consignor">
            <!-- Label and name on ONE line, same shape as the outcome row. -->
            <div class="ia-lot__consignorRow">
              <!-- "Consignor" at Tattersalls, "Vendor" at Arqana: house DATA. -->
              <span class="ia-lot__consignorLabel">
                @if (consignorLabel) {
                  {{ consignorLabel }}
                } @else {
                  {{ 'international_auctions.sale.consignor' | translate }}
                }
              </span>
              @if (consignorName) {
                <span class="ia-lot__consignorName">{{ consignorName }}</span>
              }
            </div>

            @if (contactName || contactPhone || contactEmail) {
              <dl class="ia-lot__contact">
                @if (contactName) {
                  <dt>{{ 'common.name' | translate }}</dt>
                  <dd>{{ contactName }}</dd>
                }
                @if (contactPhone) {
                  <dt>{{ 'common.phone' | translate }}</dt>
                  <dd>
                    <a class="ia-lot__link" [href]="'tel:' + contactPhone">{{
                      contactPhone
                    }}</a>
                  </dd>
                }
                @if (contactEmail) {
                  <dt>{{ 'common.email' | translate }}</dt>
                  <dd>
                    <a class="ia-lot__link" [href]="'mailto:' + contactEmail">{{
                      contactEmail
                    }}</a>
                  </dd>
                }
              </dl>
            }
          </div>
        }

      </div>
    </div>

    <!-- ===== Horse details ============================================= -->
    <!-- BELOW the hero, at the full width of the page, filling the band
         between the media stage and the tab strip. It used to sit in the
         identity column beside the stage, which left the opposite half of the
         viewport empty all the way down and gave the description a cell a
         quarter of the page wide to hold a two-hundred-word paragraph.

         A row with no value is ABSENT — a withdrawn lot would otherwise print
         fifteen em dashes. `raw_details` is appended verbatim, because an
         unmapped field still has to reach the screen. -->
    @if (detailRows.length > 0 || descriptionText) {
      <div class="ia-lot__detailsWrap">
        <div class="ia-lot__details">
          <div class="ia-lot__detailsHead">
            {{ 'international_auctions.lot.details' | translate }}
          </div>

          @if (detailRows.length > 0) {
            <!-- ONE label/value pair per `tr`. How many pairs share a line is
                 the stylesheet's business: `tbody` is a grid of alternating
                 label/value tracks and every `tr` is `display: contents`, so
                 three pairs fit a wide page, two a tablet and one a phone
                 WITHOUT the component having to guess a column count. That
                 also retires the `null` padding pair the old fold needed to
                 keep its columns aligned.

                 The roles are explicit because they have to survive exactly
                 that: changing the display of a table element drops its
                 implicit role. Spelled out, the table stays a table and each
                 row stays a row to a screen reader at every width. -->
            <table class="ia-lot__table" role="table">
              <tbody role="rowgroup">
                @for (row of detailRows; track $index) {
                  <tr role="row">
                    <th scope="row" role="rowheader">
                      @if (row.labelKey) {
                        {{ row.labelKey | translate }}
                      } @else {
                        {{ row.labelText }}
                      }
                    </th>
                    <!-- A translated value belongs to exactly one field:
                         declarations, where "None declared" is the house's
                         disclosure and not missing data. -->
                    <td role="cell" [class.is-mono]="row.mono">
                      @if (row.valueKey) {
                        <span class="ia-lot__cellNone">{{
                          row.valueKey | translate
                        }}</span>
                      } @else {
                        {{ row.value }}
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          }

          <!-- The house's prose, full width and set as a paragraph rather than
               squeezed into a value cell.

               `dir="auto"` because the paragraph's language is the HOUSE's, not
               the visitor's: an English description sitting in the Arabic page
               is resolved LTR and reads correctly, instead of having every
               sentence's full stop thrown to the head of its line by the RTL
               container. When the description itself is Arabic the same
               attribute resolves it RTL, so this needs no revisiting. -->
          @if (descriptionText) {
            <div class="ia-lot__description">
              <h2 class="ia-lot__descriptionHead">
                {{ 'international_auctions.lot.description' | translate }}
              </h2>
              <p class="ia-lot__descriptionBody" dir="auto">
                {{ descriptionText }}
              </p>
            </div>
          }
        </div>
      </div>
    }

    <!-- ===== Tabs ====================================================== -->
    <!-- Built in the component from what the lot actually holds, so a tab with
         nothing behind it is absent rather than present-and-empty. -->
    @if (tabs.length > 0) {
      <div class="ia-lot__tabsWrap">
        <div class="ia-lot__tabs" role="tablist">
          @for (tab of tabs; track tab.id) {
            <button
              type="button"
              role="tab"
              class="ia-lot__tab"
              [class.is-active]="tab.id === activeTabId"
              [attr.aria-selected]="tab.id === activeTabId"
              (click)="selectTab(tab.id)"
            >
              {{ tab.labelKey | translate }}
            </button>
          }
        </div>

        <div class="ia-lot__panel" role="tabpanel">
          @if (activeTabId === 'pedigree') {
            <ul class="ia-lot__docs">
              @for (doc of pedigreeDocuments; track doc.id) {
                <li class="ia-lot__doc">
                  <div class="ia-lot__docBar">
                    <span class="ia-lot__docText">
                      <span class="ia-lot__docHead">
                        <span class="ia-lot__docName">{{ doc.fileName }}</span>
                        <!-- Access type. Two states only in this module: free
                             to download, or behind a sign-in. -->
                        <span
                          class="ia-lot__access"
                          [class.is-locked]="doc.isLocked"
                          >{{ doc.accessKey | translate }}</span
                        >
                      </span>
                      <span class="ia-lot__docSub">
                        @if (doc.category) {
                          <span>{{ doc.category }}</span>
                        }
                        @if (doc.dateLabel) {
                          <span>{{ doc.dateLabel }}</span>
                        }
                        @if (doc.sizeLabel) {
                          <span>{{ doc.sizeLabel }}</span>
                        }
                      </span>
                    </span>
                    <!-- A locked file offers the way IN, never a download link
                         that would 401. Signed in, both types behave alike.
                         The wording comes from the row, because a repository
                         link is opened, not downloaded. -->
                    @if (doc.isLocked && !isSignedIn) {
                      <a
                        class="ia-lot__docLink"
                        [routerLink]="signInLink | localeLink"
                        >{{ doc.lockedActionKey | translate }}</a
                      >
                    } @else {
                      <a
                        class="ia-lot__docLink"
                        [href]="doc.fileUrl"
                        target="_blank"
                        rel="noopener"
                        >{{ doc.actionKey | translate }}</a
                      >
                    }
                  </div>

                  <!-- Two languages of the SAME document, so one row and a
                       toggle rather than two rows. It only exists when a second
                       file really does — a button offering an Arabic pedigree
                       nobody has produced yet is worse than no button — and it
                       switches the download link as well as the preview. -->
                  @if (doc.variants.length > 1) {
                    <div
                      class="ia-lot__docLangs"
                      role="group"
                      [attr.aria-label]="
                        'international_auctions.lot.doc_language' | translate
                      "
                    >
                      @for (variant of doc.variants; track variant.locale) {
                        <button
                          type="button"
                          class="ia-lot__docLang"
                          [class.is-active]="variant.locale === doc.activeLocale"
                          [attr.aria-pressed]="
                            variant.locale === doc.activeLocale
                          "
                          (click)="selectDocumentLocale(doc, variant.locale)"
                        >
                          {{ variant.labelKey | translate }}
                        </button>
                      }
                    </div>
                  }

                  <!-- The document itself, rendered ON THE PAGE. No click to
                       reach it: a catalogue page is the content, not an
                       attachment. A locked file shows nothing until the
                       visitor signs in, and a format the browser cannot render
                       has no frame at all — only its download link above. -->
                  @if (doc.kind !== 'other' && (!doc.isLocked || isSignedIn)) {
                    <div class="ia-lot__docPreview" [class.is-image]="doc.kind === 'image'">
                      @if (doc.kind === 'image') {
                        <img
                          class="ia-lot__docImage"
                          [src]="doc.fileUrl"
                          [alt]="doc.fileName"
                          loading="lazy"
                          decoding="async"
                        />
                      } @else {
                        <iframe
                          class="ia-lot__docFrame"
                          [src]="doc.fileUrl | safeUrl"
                          [title]="doc.fileName"
                          loading="lazy"
                        ></iframe>
                      }
                    </div>
                  }
                </li>
              }
            </ul>
          } @else if (activeTabId === 'video') {
            <div class="ia-lot__videos">
              @for (video of videos; track video.id) {
                <div class="ia-lot__video">
                  @if (isPlaying(video)) {
                    @if (video.kind === 'external') {
                      <iframe
                        class="ia-lot__embed"
                        [src]="video.url | safeUrl"
                        [title]="video.title || title"
                        allow="accelerometer; encrypted-media; picture-in-picture"
                        allowfullscreen
                      ></iframe>
                    } @else {
                      <video
                        class="ia-lot__embed"
                        [src]="video.url"
                        [attr.poster]="video.poster_url"
                        controls
                      ></video>
                    }
                  } @else {
                    <button
                      type="button"
                      class="ia-lot__play"
                      [style.--ia-brand]="brandColor"
                      (click)="playVideo(video)"
                    >
                      @if (video.poster_url) {
                        <img
                          class="ia-lot__stageImg"
                          [src]="video.poster_url"
                          [alt]="video.title || title"
                          loading="lazy"
                          decoding="async"
                        />
                      }
                      <span class="ia-lot__playGlyph" aria-hidden="true"
                        >&#9654;</span
                      >
                      <span class="ia-lot__playLabel">{{
                        video.title ||
                          ('international_auctions.lot.tab_video' | translate)
                      }}</span>
                    </button>
                  }
                </div>
              }
            </div>
          } @else if (activeTabId === 'documents') {
            <ul class="ia-lot__docs">
              @for (doc of otherDocuments; track doc.id) {
                <li class="ia-lot__doc">
                  <div class="ia-lot__docBar">
                    <span class="ia-lot__docText">
                      <span class="ia-lot__docHead">
                        <span class="ia-lot__docName">{{ doc.fileName }}</span>
                        <!-- Access type. Two states only in this module: free
                             to download, or behind a sign-in. -->
                        <span
                          class="ia-lot__access"
                          [class.is-locked]="doc.isLocked"
                          >{{ doc.accessKey | translate }}</span
                        >
                      </span>
                      <span class="ia-lot__docSub">
                        @if (doc.category) {
                          <span>{{ doc.category }}</span>
                        }
                        @if (doc.dateLabel) {
                          <span>{{ doc.dateLabel }}</span>
                        }
                        @if (doc.sizeLabel) {
                          <span>{{ doc.sizeLabel }}</span>
                        }
                      </span>
                    </span>
                    <!-- A locked file offers the way IN, never a download link
                         that would 401. Signed in, both types behave alike.
                         The wording comes from the row, because a repository
                         link is opened, not downloaded. -->
                    @if (doc.isLocked && !isSignedIn) {
                      <a
                        class="ia-lot__docLink"
                        [routerLink]="signInLink | localeLink"
                        >{{ doc.lockedActionKey | translate }}</a
                      >
                    } @else {
                      <a
                        class="ia-lot__docLink"
                        [href]="doc.fileUrl"
                        target="_blank"
                        rel="noopener"
                        >{{ doc.actionKey | translate }}</a
                      >
                    }
                  </div>

                  <!-- Two languages of the SAME document, so one row and a
                       toggle rather than two rows. It only exists when a second
                       file really does — a button offering an Arabic pedigree
                       nobody has produced yet is worse than no button — and it
                       switches the download link as well as the preview. -->
                  @if (doc.variants.length > 1) {
                    <div
                      class="ia-lot__docLangs"
                      role="group"
                      [attr.aria-label]="
                        'international_auctions.lot.doc_language' | translate
                      "
                    >
                      @for (variant of doc.variants; track variant.locale) {
                        <button
                          type="button"
                          class="ia-lot__docLang"
                          [class.is-active]="variant.locale === doc.activeLocale"
                          [attr.aria-pressed]="
                            variant.locale === doc.activeLocale
                          "
                          (click)="selectDocumentLocale(doc, variant.locale)"
                        >
                          {{ variant.labelKey | translate }}
                        </button>
                      }
                    </div>
                  }

                  <!-- The document itself, rendered ON THE PAGE. No click to
                       reach it: a catalogue page is the content, not an
                       attachment. A locked file shows nothing until the
                       visitor signs in, and a format the browser cannot render
                       has no frame at all — only its download link above. -->
                  @if (doc.kind !== 'other' && (!doc.isLocked || isSignedIn)) {
                    <div class="ia-lot__docPreview" [class.is-image]="doc.kind === 'image'">
                      @if (doc.kind === 'image') {
                        <img
                          class="ia-lot__docImage"
                          [src]="doc.fileUrl"
                          [alt]="doc.fileName"
                          loading="lazy"
                          decoding="async"
                        />
                      } @else {
                        <iframe
                          class="ia-lot__docFrame"
                          [src]="doc.fileUrl | safeUrl"
                          [title]="doc.fileName"
                          loading="lazy"
                        ></iframe>
                      }
                    </div>
                  }
                </li>
              }
            </ul>
          } @else if (activeTabId === 'updates') {
            <!-- Newest amendment first: the latest correction is the one a
                 buyer is looking for. -->
            <ol class="ia-lot__timeline">
              @for (update of updates; track update.id) {
                <li class="ia-lot__update">
                  <span class="ia-lot__updateDate">{{
                    'international_auctions.lot.published_on'
                      | translate: update.params
                  }}</span>
                  <p class="ia-lot__updateBody">{{ update.body }}</p>
                </li>
              }
            </ol>
          }
        </div>
      </div>
    }

    <!-- ===== My notes =================================================== -->
    <!--
      A SECTION OF ITS OWN, deliberately not a tab.

      Every tab above describes something the auction house published — the
      pedigree, the vet certificates, the amendments. This is the opposite: it
      is the visitor's own writing about the lot, it belongs to them, and
      filing it beside the house's paperwork would say it is one more document
      Tattersalls sent us.

      It also means the panel is always on screen rather than behind a click,
      and it never competes with the pedigree for which tab opens first.
    -->
    @if (lotId) {
      <app-international-lot-notes
        [lotId]="lotId"
      ></app-international-lot-notes>
    }
  } @else {
    <!-- `getLot()` answers `null` for an unknown lot rather than throwing, so
         this is a render and not an error handler. -->
    <div class="ia-lot__empty">
      <span class="ia-lot__emptyMark" aria-hidden="true">&#9633;</span>
      <p class="ia-lot__emptyTitle">
        {{ 'international_auctions.empty.lot_not_found' | translate }}
      </p>
      <p class="ia-lot__emptyHint">
        {{ 'international_auctions.empty.lot_not_found_hint' | translate }}
      </p>
      <div class="ia-lot__emptyActions">
        <a class="ia-lot__emptyCta" [routerLink]="saleLink | localeLink">{{
          'international_auctions.card.view_catalogue' | translate
        }}</a>
        <a class="ia-lot__emptyBack" [routerLink]="indexLink | localeLink">{{
          'international_auctions.back_to_index' | translate
        }}</a>
      </div>
    </div>
  }
</section>
